import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  MessageCircle,
  Send,
  Users,
  Hash,
  Circle,
  Search,
  Plus,
  AlertTriangle,
  ShoppingCart,
  Factory,
  ChevronRight,
  Settings,
  UserPlus,
  UserMinus,
  X,
  Crown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://") + "/api/chat/ws";

const CHANNEL_ICONS = {
  hash: Hash,
  factory: Factory,
  "shopping-cart": ShoppingCart,
  "alert-triangle": AlertTriangle,
  users: Users,
};

export default function Messages() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({ total: 0, by_conversation: {} });
  const [isConnected, setIsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user?.id) return;
    
    const token = localStorage.getItem("mes_token");
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/${user.id}?token=${token}`);
    
    ws.onopen = () => {
      setIsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        const msg = data.message;
        
        if (selectedChat?.id === msg.conversation_id) {
          setMessages(prev => [...prev, msg]);
          markAsRead(msg.conversation_id);
        } else {
          setUnreadCounts(prev => ({
            total: prev.total + 1,
            by_conversation: {
              ...prev.by_conversation,
              [msg.conversation_id]: (prev.by_conversation[msg.conversation_id] || 0) + 1
            }
          }));
          
          if (msg.sender_id !== user.id) {
            playNotificationSound();
            toast.info(`${msg.sender_name}: ${msg.content.substring(0, 50)}...`);
          }
        }
        
        // Refresh lists
        fetchChannels();
        fetchConversations();
      } else if (data.type === "typing") {
        if (selectedChat?.id === data.conversation_id && data.user_id !== user.id) {
          setIsTyping(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 3000);
        }
      }
    };
    
    ws.onclose = () => {
      setIsConnected(false);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    wsRef.current = ws;
  }, [user?.id, selectedChat?.id]);

  useEffect(() => {
    connectWebSocket();
    fetchChannels();
    fetchConversations();
    fetchUnreadCounts();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API}/chat/channels?app_source=mes`);
      setChannels(res.data);
      
      // Auto-join default channels
      for (const channel of res.data) {
        if (channel.is_default && !channel.members?.includes(user.id)) {
          try {
            await axios.post(`${API}/chat/channels/${channel.id}/join?user_id=${user.id}`);
          } catch (e) {}
        }
      }
      
      const refreshed = await axios.get(`${API}/chat/channels?app_source=mes`);
      setChannels(refreshed.data);
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations?user_id=${user.id}`);
      setConversations(res.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const res = await axios.get(`${API}/chat/unread?user_id=${user.id}`);
      setUnreadCounts(res.data);
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const res = await axios.get(`${API}/chat/users/available?current_user_id=${user.id}`);
      setAvailableUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${conversationId}?limit=100`);
      setMessages(res.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      await axios.post(`${API}/chat/messages/mark-read?conversation_id=${conversationId}&user_id=${user.id}`);
      setUnreadCounts(prev => ({
        total: Math.max(0, prev.total - (prev.by_conversation[conversationId] || 0)),
        by_conversation: { ...prev.by_conversation, [conversationId]: 0 }
      }));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const openChat = (chat) => {
    setSelectedChat(chat);
    setShowNewChat(false);
    setShowMembers(false);
    fetchMessages(chat.id);
    markAsRead(chat.id);
    
    // Se for canal, carregar membros
    if (chat.type === "channel") {
      fetchChannelMembers(chat.id);
    }
  };

  const fetchChannelMembers = async (channelId) => {
    try {
      // Buscar detalhes do canal para obter lista de membros
      const channelRes = await axios.get(`${API}/chat/channels?app_source=mes`);
      const channel = channelRes.data.find(c => c.id === channelId);
      
      if (channel && channel.members) {
        // Buscar informações dos utilizadores
        const usersRes = await axios.get(`${API}/chat/users/available?current_user_id=${user.id}`);
        const allUsersData = [...usersRes.data, { id: user.id, name: user.name, role: user.role }];
        
        const members = channel.members.map(memberId => {
          const userData = allUsersData.find(u => u.id === memberId);
          return {
            id: memberId,
            name: userData?.name || "Utilizador",
            role: userData?.role || "operator",
            is_admin: channel.admins?.includes(memberId) || false,
            is_online: userData?.is_online || memberId === user.id
          };
        });
        
        setChannelMembers(members);
        setAllUsers(allUsersData);
      }
    } catch (error) {
      console.error("Error fetching channel members:", error);
    }
  };

  const addMemberToChannel = async (userId) => {
    if (!selectedChat || selectedChat.type !== "channel") return;
    
    try {
      await axios.post(`${API}/chat/channels/${selectedChat.id}/join?user_id=${userId}`);
      toast.success("Utilizador adicionado ao canal");
      fetchChannelMembers(selectedChat.id);
      fetchChannels();
    } catch (error) {
      toast.error("Erro ao adicionar utilizador");
    }
  };

  const removeMemberFromChannel = async (userId) => {
    if (!selectedChat || selectedChat.type !== "channel") return;
    
    try {
      await axios.post(`${API}/chat/channels/${selectedChat.id}/leave?user_id=${userId}`);
      toast.success("Utilizador removido do canal");
      fetchChannelMembers(selectedChat.id);
      fetchChannels();
    } catch (error) {
      toast.error("Erro ao remover utilizador");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    const messageData = {
      action: "send_message",
      conversation_id: selectedChat.id,
      content: newMessage.trim(),
      sender_name: user.name,
      sender_role: user.role,
      app_source: "mes"
    };
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(messageData));
      setNewMessage("");
    } else {
      try {
        await axios.post(
          `${API}/chat/messages?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&user_role=${user.role}&app_source=mes`,
          { conversation_id: selectedChat.id, content: newMessage.trim(), message_type: "text" }
        );
        setNewMessage("");
        fetchMessages(selectedChat.id);
      } catch (error) {
        toast.error("Erro ao enviar mensagem");
      }
    }
  };

  const startConversation = async (otherUser) => {
    try {
      const res = await axios.post(
        `${API}/chat/conversations?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        { participant_id: otherUser.id }
      );
      openChat({ ...res.data, name: otherUser.name, type: "private" });
      fetchConversations();
    } catch (error) {
      toast.error("Erro ao iniciar conversa");
    }
  };

  const sendTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedChat) {
      wsRef.current.send(JSON.stringify({
        action: "typing",
        conversation_id: selectedChat.id,
        user_name: user.name
      }));
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQYTYIbj9cV9Jw5eeejxi0khbZzT44xSJFWq4+WBPxR2uc7OfUEYgMfH0X5BGoTHwtF/QxuEycHRf0MdhMrA0X9DHoTLwNF/QiKGzL7PfUEkh86929BvKijH1tPKVyYywdbTy1koMMTX1M5aJi7D2NbRXCQswdnW0V0jKsHZ1tFdIynA2tbRXiMpv9rW0V8jKb/a1tFfIym/2tXRXyMpv9rV0V8jKb/a1dFfIym/2tXRXyMowtrU0V8gJ8fa09JeICfH2tLSXiAmx9rR014gJsjb0NJdICXI29DSXSAlydvP0l0gJcnbz9JdICTK28/SXSAkytvP0l0gJMrbz9JdICTK28/SXSAkydvP0l0gJMnbz9JdICTJ28/SXQ==");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getChannelIcon = (iconName) => {
    return CHANNEL_ICONS[iconName] || Hash;
  };

  const filteredChannels = channels.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredConversations = conversations.filter(c => 
    c.participant_names?.some(n => n.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Mensagens</h1>
          <p className="text-muted-foreground">Chat interno da equipa</p>
        </div>
        <div className="flex items-center gap-2">
          <Circle className={`h-3 w-3 ${isConnected ? "fill-green-500 text-green-500" : "fill-red-500 text-red-500"}`} />
          <span className="text-sm text-muted-foreground">{isConnected ? "Conectado" : "Desconectado"}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Left Panel - Conversations List */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Conversas</CardTitle>
              <Button 
                size="sm" 
                onClick={() => { setShowNewChat(true); fetchAvailableUsers(); }}
                data-testid="new-chat-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Nova
              </Button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {/* New Chat - Users List */}
              {showNewChat && (
                <div className="p-3 border-b">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground">NOVA CONVERSA</p>
                    <Button variant="ghost" size="sm" onClick={() => setShowNewChat(false)}>Cancelar</Button>
                  </div>
                  {availableUsers.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary cursor-pointer"
                      onClick={() => startConversation(u)}
                    >
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center relative">
                        <span className="text-lg font-semibold text-primary">{u.name?.charAt(0).toUpperCase()}</span>
                        {u.is_online && <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-background" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.role === "admin" ? "Administrador" : "Operador"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Channels */}
              <div className="p-3">
                <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">CANAIS</p>
                {filteredChannels.map(channel => {
                  const IconComponent = getChannelIcon(channel.icon);
                  const unread = unreadCounts.by_conversation[channel.id] || 0;
                  const isSelected = selectedChat?.id === channel.id;
                  return (
                    <div
                      key={channel.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-primary/10" : "hover:bg-secondary"}`}
                      onClick={() => openChat({ ...channel, type: "channel" })}
                      data-testid={`channel-${channel.name.toLowerCase()}`}
                    >
                      <div 
                        className="h-10 w-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: channel.color + "20" }}
                      >
                        <IconComponent className="h-5 w-5" style={{ color: channel.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{channel.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{channel.last_message || channel.description}</p>
                      </div>
                      {unread > 0 && <Badge className="bg-red-500">{unread}</Badge>}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  );
                })}
              </div>

              {/* Direct Messages */}
              {filteredConversations.length > 0 && (
                <div className="p-3 border-t">
                  <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">MENSAGENS DIRETAS</p>
                  {filteredConversations.map(conv => {
                    const otherName = conv.participant_names?.find((_, i) => conv.participants[i] !== user.id) || "Utilizador";
                    const unread = unreadCounts.by_conversation[conv.id] || 0;
                    const isSelected = selectedChat?.id === conv.id;
                    return (
                      <div
                        key={conv.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-primary/10" : "hover:bg-secondary"}`}
                        onClick={() => openChat({ ...conv, name: otherName, type: "private" })}
                      >
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-lg font-semibold text-primary">{otherName.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{otherName}</p>
                          <p className="text-xs text-muted-foreground truncate">{conv.last_message || "Sem mensagens"}</p>
                        </div>
                        {unread > 0 && <Badge className="bg-red-500">{unread}</Badge>}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Chat */}
        <Card className="lg:col-span-2 flex flex-col">
          {selectedChat ? (
            <>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center gap-3">
                  {selectedChat.type === "channel" ? (
                    <div 
                      className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: (selectedChat.color || "#3B82F6") + "20" }}
                    >
                      {React.createElement(getChannelIcon(selectedChat.icon), {
                        className: "h-6 w-6",
                        style: { color: selectedChat.color || "#3B82F6" }
                      })}
                    </div>
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xl font-semibold text-primary">{selectedChat.name?.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <CardTitle>{selectedChat.name}</CardTitle>
                    {isTyping && <p className="text-sm text-muted-foreground">{isTyping} está a escrever...</p>}
                    {!isTyping && selectedChat.type === "channel" && (
                      <p className="text-sm text-muted-foreground">
                        {channelMembers.length} {channelMembers.length === 1 ? "membro" : "membros"}
                      </p>
                    )}
                    {!isTyping && selectedChat.type !== "channel" && selectedChat.description && (
                      <p className="text-sm text-muted-foreground">{selectedChat.description}</p>
                    )}
                  </div>
                  {selectedChat.type === "channel" && (
                    <Button
                      variant={showMembers ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowMembers(!showMembers)}
                      data-testid="show-members-btn"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Membros
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              <div className="flex-1 flex overflow-hidden">
                {/* Messages Area */}
                <div className={`flex-1 flex flex-col ${showMembers ? "border-r" : ""}`}>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full p-4">
                      <div className="space-y-4">
                        {messages.length === 0 && (
                          <div className="text-center py-12 text-muted-foreground">
                            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>Nenhuma mensagem ainda</p>
                            <p className="text-sm">Seja o primeiro a enviar uma mensagem!</p>
                          </div>
                        )}
                    {messages.map((msg, idx) => {
                      const isOwn = msg.sender_id === user.id;
                      const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                      const showDate = idx === 0 || 
                        new Date(msg.created_at).toDateString() !== new Date(messages[idx - 1]?.created_at).toDateString();
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="flex justify-center my-4">
                              <Badge variant="secondary" className="text-xs">
                                {new Date(msg.created_at).toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                              </Badge>
                            </div>
                          )}
                          <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
                              {showAvatar && !isOwn && (
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-xs font-semibold text-primary">{msg.sender_name?.charAt(0)}</span>
                                  </div>
                                  <span className="text-xs font-medium text-foreground">{msg.sender_name}</span>
                                  {msg.app_source !== "mes" && (
                                    <Badge variant="outline" className="text-[10px] px-1">{msg.app_source}</Badge>
                                  )}
                                </div>
                              )}
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  isOwn 
                                    ? "bg-primary text-primary-foreground rounded-br-md" 
                                    : "bg-secondary text-secondary-foreground rounded-bl-md"
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                              </div>
                              <span className="text-[10px] text-muted-foreground mt-1 mx-1">
                                {new Date(msg.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Escreva uma mensagem..."
                    value={newMessage}
                    onChange={(e) => { setNewMessage(e.target.value); sendTyping(); }}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button onClick={sendMessage} disabled={!newMessage.trim()} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
                </div>
                
                {/* Members Panel */}
                {showMembers && selectedChat?.type === "channel" && (
                  <div className="w-72 bg-secondary/30 flex flex-col" data-testid="members-panel">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="font-semibold">Membros ({channelMembers.length})</h3>
                      <Button variant="ghost" size="icon" onClick={() => setShowMembers(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <ScrollArea className="flex-1">
                      {/* Current Members */}
                      <div className="p-3">
                        <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">NO CANAL</p>
                        {channelMembers.map(member => (
                          <div
                            key={member.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary"
                          >
                            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center relative">
                              <span className="text-sm font-semibold text-primary">{member.name?.charAt(0).toUpperCase()}</span>
                              {member.is_online && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 bg-green-500 rounded-full border-2 border-background" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{member.name}</p>
                                {member.is_admin && <Crown className="h-3 w-3 text-yellow-500" />}
                                {member.id === user.id && <Badge variant="outline" className="text-[10px]">Você</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {member.role === "admin" ? "Administrador" : member.role === "shift_leader" ? "Chefe Turno" : "Operador"}
                              </p>
                            </div>
                            {member.id !== user.id && user.role === "admin" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100"
                                onClick={() => removeMemberFromChannel(member.id)}
                                title="Remover do canal"
                              >
                                <UserMinus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Add Members */}
                      {user.role === "admin" && (
                        <div className="p-3 border-t">
                          <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">ADICIONAR</p>
                          {allUsers
                            .filter(u => !channelMembers.find(m => m.id === u.id))
                            .map(u => (
                              <div
                                key={u.id}
                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary"
                              >
                                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                                  <span className="text-sm font-semibold text-muted-foreground">{u.name?.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{u.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {u.role === "admin" ? "Administrador" : "Operador"}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-500 hover:text-green-600 hover:bg-green-100"
                                  onClick={() => addMemberToChannel(u.id)}
                                  title="Adicionar ao canal"
                                >
                                  <UserPlus className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          {allUsers.filter(u => !channelMembers.find(m => m.id === u.id)).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              Todos os utilizadores já estão no canal
                            </p>
                          )}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-foreground mb-2">Selecione uma conversa</h3>
                <p className="text-sm">Escolha um canal ou inicie uma nova conversa</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
